import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loader2, RefreshCw, AlertTriangle, Activity, GitMerge, Check, ExternalLink } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { formatEdgeForDisplay, conflictKind, type EdgeForDisplay } from "@/components/research-hub/labels";

type DriftAlert = {
  id: string;
  detected_at: string;
  severity: string;
  status: string;
  summary: string | null;
  resolution_notes: string | null;
  resolved_at: string | null;
  claim_edge_id: string | null;
  original_source_id: string | null;
  new_evidence_source_id: string | null;
};

type Snapshot = {
  id: string;
  source_type: string;
  period_start: string;
  period_end: string;
  sample_count: number;
  cosine_distance_from_baseline: number | null;
  is_baseline: boolean | null;
  notes: string | null;
  created_at: string;
};

type Conflict = {
  id: string;
  created_at: string;
  kind: string;
  status: string;
  notes: string | null;
  edge_id_a: string | null;
  edge_id_b: string | null;
};

const TABS = [
  { value: "thesis", label: "Thesis drift" },
  { value: "embedding", label: "Embedding drift" },
  { value: "conflicts", label: "KG conflicts" },
];

function severityBadge(s: string) {
  const v = s.toLowerCase();
  if (v === "high" || v === "critical")
    return <Badge variant="destructive">{s}</Badge>;
  if (v === "medium" || v === "moderate")
    return <Badge className="bg-amber-500 text-white hover:bg-amber-500/90">{s}</Badge>;
  return <Badge variant="secondary">{s}</Badge>;
}

function statusBadge(s: string) {
  const v = s.toLowerCase();
  if (v === "resolved" || v === "closed" || v === "dismissed")
    return <Badge variant="outline" className="text-emerald-700 border-emerald-300">{s}</Badge>;
  if (v === "open" || v === "pending")
    return <Badge variant="outline" className="text-amber-700 border-amber-300">{s}</Badge>;
  return <Badge variant="outline">{s}</Badge>;
}

function distanceBadge(d: number | null) {
  if (d == null) return <Badge variant="outline">—</Badge>;
  if (d >= 0.25) return <Badge variant="destructive">{d.toFixed(3)}</Badge>;
  if (d >= 0.10)
    return <Badge className="bg-amber-500 text-white hover:bg-amber-500/90">{d.toFixed(3)}</Badge>;
  return <Badge variant="secondary">{d.toFixed(3)}</Badge>;
}

export default function ResearchDrift() {
  const [tab, setTab] = useState("thesis");
  const qc = useQueryClient();

  const { data: alerts, isLoading: loadingAlerts } = useQuery({
    queryKey: ["research_drift_alerts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_drift_alerts")
        .select("*")
        .order("detected_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as DriftAlert[];
    },
  });

  const { data: snapshots, isLoading: loadingSnaps } = useQuery({
    queryKey: ["research_embedding_drift_snapshots"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_embedding_drift_snapshots")
        .select("*")
        .order("period_end", { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as Snapshot[];
    },
  });

  const { data: conflicts, isLoading: loadingConf } = useQuery({
    queryKey: ["research_kg_conflicts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_kg_conflicts")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as Conflict[];
    },
  });

  // Resolve every referenced edge id to human labels in ONE call so the conflict
  // row reads "Energy → Inflation (supports)" — making "Keep A / Keep B" meaningful —
  // instead of "{edge_id_a} ↔ {edge_id_b}", and so each thesis-drift alert can show a
  // readable "previous claim" in its Previous → New → Conflict narrative.
  const conflictEdgeIds = Array.from(
    new Set(
      [
        ...(conflicts ?? []).flatMap((c) => [c.edge_id_a, c.edge_id_b]),
        ...(alerts ?? []).map((a) => a.claim_edge_id),
      ].filter(Boolean) as string[],
    ),
  );
  const { data: edgeLabelMap, isLoading: loadingEdgeLabels } = useQuery({
    queryKey: ["kg_edges_with_labels", conflictEdgeIds],
    enabled: conflictEdgeIds.length > 0,
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)("kg_edges_with_labels", {
        _edge_ids: conflictEdgeIds,
      });
      if (error) throw error;
      const map = new Map<string, EdgeForDisplay>();
      ((data ?? []) as EdgeForDisplay[]).forEach((e) => {
        if (e.edge_id) map.set(e.edge_id, e);
      });
      return map;
    },
  });
  const edgeLabels = edgeLabelMap ?? new Map<string, EdgeForDisplay>();

  const resolveAlert = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: "resolved" | "dismissed" }) => {
      const { error } = await supabase
        .from("research_drift_alerts")
        .update({
          status,
          resolved_at: new Date().toISOString(),
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["research_drift_alerts"] });
      toast.success("Alert updated");
    },
    onError: (e: any) => toast.error(e.message ?? "Failed to update alert"),
  });

  const resolveConflict = useMutation({
    mutationFn: async ({ id, resolution }: { id: string; resolution: string }) => {
      const { error } = await supabase
        .from("research_kg_conflicts")
        .update({
          status: "resolved",
          resolution,
          resolved_at: new Date().toISOString(),
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["research_kg_conflicts"] });
      toast.success("Conflict resolved");
    },
    onError: (e: any) => toast.error(e.message ?? "Failed to resolve"),
  });

  const runDetect = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("research-drift-detect", {
        body: {},
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (data: any) => {
      toast.success(
        `Drift scan complete: ${data?.thesis_alerts ?? 0} thesis alert(s), ${data?.snapshots ?? 0} snapshot(s)`,
      );
      qc.invalidateQueries({ queryKey: ["research_drift_alerts"] });
      qc.invalidateQueries({ queryKey: ["research_embedding_drift_snapshots"] });
    },
    onError: (e: any) => toast.error(e.message ?? "Drift scan failed"),
  });

  const summary = useMemo(() => {
    const openAlerts = (alerts ?? []).filter(
      (a) => a.status !== "resolved" && a.status !== "dismissed",
    ).length;
    const high = (alerts ?? []).filter(
      (a) => a.severity?.toLowerCase() === "high" || a.severity?.toLowerCase() === "critical",
    ).length;
    const latestDistance = (snapshots ?? [])
      .filter((s) => !s.is_baseline)
      .map((s) => s.cosine_distance_from_baseline ?? 0)[0];
    const openConflicts = (conflicts ?? []).filter((c) => c.status !== "resolved").length;
    return { openAlerts, high, latestDistance, openConflicts };
  }, [alerts, snapshots, conflicts]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Drift</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Detect when new evidence contradicts existing knowledge-graph claims
            (thesis drift) and when the underlying source corpus shifts away from
            its embedding baseline (embedding drift).
          </p>
        </div>
        <Button
          onClick={() => runDetect.mutate()}
          disabled={runDetect.isPending}
          variant="default"
        >
          {runDetect.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Run drift scan
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Open thesis alerts</CardDescription>
            <CardTitle className="text-2xl flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              {summary.openAlerts}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>High severity</CardDescription>
            <CardTitle className="text-2xl">{summary.high}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Latest embedding distance</CardDescription>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Activity className="h-5 w-5 text-muted-foreground" />
              {summary.latestDistance != null ? summary.latestDistance.toFixed(3) : "—"}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Open KG conflicts</CardDescription>
            <CardTitle className="text-2xl flex items-center gap-2">
              <GitMerge className="h-5 w-5 text-muted-foreground" />
              {summary.openConflicts}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {tab === "thesis" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Thesis drift alerts</CardTitle>
            <CardDescription>
              Triggered when a new source contains evidence that contradicts an
              existing knowledge-graph claim. Each alert reads as a short narrative:
              the <strong>previous claim</strong> we held, the <strong>new evidence</strong>{" "}
              that arrived, and the <strong>conflict</strong> between them.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingAlerts ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : !alerts?.length ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                No drift alerts. Run a scan to check the latest sources against
                existing claims.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Detected</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Drift narrative</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {alerts.map((a) => {
                    const claimEdge = a.claim_edge_id ? edgeLabels.get(a.claim_edge_id) : undefined;
                    return (
                    <TableRow key={a.id}>
                      <TableCell className="text-xs whitespace-nowrap align-top">
                        {format(new Date(a.detected_at), "dd/MM/yyyy HH:mm")}
                      </TableCell>
                      <TableCell className="align-top">{severityBadge(a.severity)}</TableCell>
                      <TableCell className="align-top">{statusBadge(a.status)}</TableCell>
                      <TableCell className="max-w-lg align-top">
                        <ol className="space-y-1.5 text-sm">
                          <li>
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mr-1">
                              Previous claim
                            </span>
                            {a.claim_edge_id ? (
                              <Link to="/partner/research/kg" className="text-primary hover:underline inline-flex items-center gap-1">
                                {loadingEdgeLabels && !claimEdge
                                  ? "resolving…"
                                  : formatEdgeForDisplay(claimEdge ?? { edge_id: a.claim_edge_id })}
                                <ExternalLink className="h-3 w-3" />
                              </Link>
                            ) : (
                              <span className="text-muted-foreground">not linked to a specific claim</span>
                            )}
                          </li>
                          <li className="flex items-start gap-1">
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mr-1 shrink-0">
                              New evidence
                            </span>
                            {a.new_evidence_source_id ? (
                              <Link
                                to={`/partner/research/sources?id=${a.new_evidence_source_id}`}
                                className="text-primary hover:underline"
                              >
                                a newly ingested source
                              </Link>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                            {a.original_source_id && (
                              <>
                                <span className="text-muted-foreground">vs.</span>
                                <Link
                                  to={`/partner/research/sources?id=${a.original_source_id}`}
                                  className="text-primary hover:underline"
                                >
                                  the original source
                                </Link>
                              </>
                            )}
                          </li>
                          <li>
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mr-1">
                              Conflict
                            </span>
                            <span>{a.summary ?? "Contradiction detected — see linked sources."}</span>
                          </li>
                        </ol>
                        {a.resolution_notes && (
                          <p className="text-xs text-muted-foreground italic mt-1">
                            {a.resolution_notes}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-right align-top">
                        {a.status !== "resolved" && a.status !== "dismissed" ? (
                          <div className="flex gap-1 justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                resolveAlert.mutate({ id: a.id, status: "resolved" })
                              }
                            >
                              <Check className="h-3 w-3 mr-1" />
                              Resolve
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                resolveAlert.mutate({ id: a.id, status: "dismissed" })
                              }
                            >
                              Dismiss
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            {a.resolved_at &&
                              format(new Date(a.resolved_at), "dd/MM/yyyy")}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {tab === "embedding" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Embedding drift snapshots</CardTitle>
            <CardDescription>
              Cosine distance of the corpus centroid versus the baseline. Distances
              above ~0.10 suggest a meaningful topical shift; above ~0.25 suggests a
              re-baseline may be needed.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingSnaps ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : !snapshots?.length ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                No snapshots yet. Run a scan to compute the first baseline.
              </p>
            ) : (
              <>
              {/* Inline threshold key so the raw distance number is readable in place,
                  not just a colour. 0 = identical to baseline; 1 = unrelated. */}
              <div className="flex flex-wrap items-center gap-3 mb-3 text-xs text-muted-foreground">
                <span>Cosine distance from baseline:</span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-2 w-2 rounded-full bg-secondary" />
                  below 0.10 — stable
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-2 w-2 rounded-full bg-amber-500" />
                  0.10–0.25 — topical shift, worth watching
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-2 w-2 rounded-full bg-destructive" />
                  above 0.25 — large shift, consider re-baselining
                </span>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead>Source type</TableHead>
                    <TableHead>Samples</TableHead>
                    <TableHead>Distance</TableHead>
                    <TableHead>Baseline?</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {snapshots.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="text-xs whitespace-nowrap">
                        {format(new Date(s.period_start), "dd/MM/yyyy")}
                        {" → "}
                        {format(new Date(s.period_end), "dd/MM/yyyy")}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{s.source_type}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">{s.sample_count}</TableCell>
                      <TableCell>
                        {s.is_baseline ? (
                          <Badge variant="outline">baseline</Badge>
                        ) : (
                          distanceBadge(s.cosine_distance_from_baseline)
                        )}
                      </TableCell>
                      <TableCell>
                        {s.is_baseline ? (
                          <Check className="h-4 w-4 text-emerald-600" />
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-md">
                        {s.notes ?? "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {tab === "conflicts" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Knowledge-graph conflicts</CardTitle>
            <CardDescription>
              Pairs of KG edges flagged by the extractor or merge job as
              potentially contradictory.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingConf ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : !conflicts?.length ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                No conflicts.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Detected</TableHead>
                    <TableHead>Kind</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Conflicting claims</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {conflicts.map((c) => {
                    const edgeA = c.edge_id_a ? edgeLabels.get(c.edge_id_a) : undefined;
                    const edgeB = c.edge_id_b ? edgeLabels.get(c.edge_id_b) : undefined;
                    return (
                    <TableRow key={c.id}>
                      <TableCell className="text-xs whitespace-nowrap">
                        {format(new Date(c.created_at), "dd/MM/yyyy HH:mm")}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{conflictKind(c.kind)}</Badge>
                      </TableCell>
                      <TableCell>{statusBadge(c.status)}</TableCell>
                      <TableCell className="text-xs max-w-xs">
                        {loadingEdgeLabels && !edgeA && !edgeB ? (
                          <span className="text-muted-foreground">resolving…</span>
                        ) : (
                          <div className="space-y-1">
                            <div>
                              <span className="font-semibold text-muted-foreground">A: </span>
                              {formatEdgeForDisplay(edgeA ?? { from_id: c.edge_id_a })}
                            </div>
                            <div>
                              <span className="font-semibold text-muted-foreground">B: </span>
                              {formatEdgeForDisplay(edgeB ?? { from_id: c.edge_id_b })}
                            </div>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-md">
                        {c.notes ?? "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        {c.status !== "resolved" && (
                          <div className="flex gap-1 justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                resolveConflict.mutate({
                                  id: c.id,
                                  resolution: "kept_a",
                                })
                              }
                            >
                              Keep A
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                resolveConflict.mutate({
                                  id: c.id,
                                  resolution: "kept_b",
                                })
                              }
                            >
                              Keep B
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                resolveConflict.mutate({
                                  id: c.id,
                                  resolution: "merged",
                                })
                              }
                            >
                              Merge
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
